import { useState } from 'react';
import { motion } from 'framer-motion';
import { LogIn, Eye, EyeOff, KeyRound, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/Dialog';
import { login } from '@/lib/auth';
import type { ApiError } from '@/lib/api';

export function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow]         = useState(false);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await login(email, password);
      window.location.href = '/';
    } catch (e) {
      const ae = e as ApiError;
      setErr(t(`error.${ae.code}`, { defaultValue: ae.message }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-canvas">
      {/* ── Left · brand panel (hidden on small screens) ───────────────────── */}
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden p-12 text-on-dark bg-gradient-to-br from-brand-green via-brand-green-deep to-brand-green-ink">
        {/* decorative shapes */}
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-brand-green-soft/30 blur-2xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-16 size-72 rounded-full bg-white/5" />
        <img
          aria-hidden
          src="/logica-erp-logogram-white.png"
          alt=""
          className="pointer-events-none absolute -bottom-8 right-4 w-72 select-none opacity-[0.08]"
          draggable={false}
        />

        {/* top · logo + kicker */}
        <div className="relative">
          <img
            src="/logica-erp-wordmark-white.png"
            alt="Logica ERP"
            className="h-9 w-auto select-none"
            draggable={false}
          />
          <p className="mt-3 font-mono text-micro-uppercase tracking-widest text-brand-green-soft">
            {t('login.kicker', 'ERP Platform')}
          </p>
        </div>

        {/* middle · headline + supporting copy */}
        <div className="relative max-w-lg">
          <h2 className="font-display text-heading-1 font-bold leading-[1.08] tracking-tight text-on-dark">
            {t('login.headline', 'The operational backbone the whole suite runs on.')}
          </h2>
          <p className="mt-6 text-subtitle leading-relaxed text-brand-green-soft">
            {t('login.subhead', 'Logica ERP brings your operations, finance, and resources into one source of truth — with a copilot inside the ledger that drafts, reconciles, and answers questions in plain words.')}
          </p>
        </div>

        {/* bottom · footer line */}
        <p className="relative text-caption text-on-dark-muted">
          {t('login.footer', 'One system. Every module. One source of truth.')}
        </p>
      </aside>

      {/* ── Right · form panel ─────────────────────────────────────────────── */}
      <main className="flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className="w-full max-w-[400px]"
        >
          {/* mobile-only logo (left panel is hidden below lg) */}
          <img
            src="/logica-erp-wordmark.png"
            alt="Logica ERP"
            className="mb-8 h-10 w-auto select-none lg:hidden"
            draggable={false}
          />

          <h1 className="text-heading-2 text-ink">{t('login.welcome', 'Welcome back')}</h1>
          <p className="mt-2 text-body-sm text-slate">
            {t('login.subtitle', 'Sign in to continue')}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <Field label={t('login.email', 'Email')} htmlFor="email">
              <Input
                id="email" type="email" autoComplete="email" required
                placeholder="nama@perusahaan.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label={t('login.password', 'Password')} htmlFor="password">
              <div className="relative">
                <Input
                  id="password" type={show ? 'text' : 'password'}
                  autoComplete="current-password" required minLength={8}
                  placeholder="••••••••" className="pr-10"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? t('login.hide_password', 'Hide password') : t('login.show_password', 'Show password')}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-stone transition-colors hover:text-ink focus:outline-none focus-visible:text-ink"
                >
                  {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </Field>
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-caption font-medium text-brand-green-deep hover:text-brand-green-ink hover:underline underline-offset-2 focus:outline-none focus-visible:underline"
              >
                {t('login.forgot', 'Forgot password?')}
              </button>
            </div>
            {err && (
              <div className="rounded-md bg-brand-error/10 text-brand-error text-caption px-3 py-2">
                {err}
              </div>
            )}
            <Button type="submit" size="lg" loading={busy} className="w-full">
              {!busy && <LogIn className="size-4" />}
              {busy ? t('login.signing_in', 'Signing in…') : t('login.sign_in', 'Sign in')}
            </Button>
          </form>

          <p className="mt-6 text-caption text-stone">
            {t('login.help', 'Need access? Contact your administrator.')}
          </p>
        </motion.div>
      </main>

      {forgotOpen && (
        <ForgotPasswordDialog defaultEmail={email} onClose={() => setForgotOpen(false)} />
      )}
    </div>
  );
}

/**
 * Forgot-password dialog. There is no self-service reset endpoint — resets are
 * admin-assisted (an admin sets a new password from Settings → Users). So
 * rather than fake a "reset link sent" flow, this explains the real process and
 * offers a prefilled mailto to request it from an administrator.
 */
function ForgotPasswordDialog({ defaultEmail, onClose }: { defaultEmail: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(defaultEmail);
  const subject = 'Password reset request — Logica ERP';
  const body =
    `Hi,\n\nI can't sign in to Logica ERP and would like my password reset.\n\n` +
    `Account email: ${email || '(fill in your account email)'}\n\nThank you.`;
  const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <div className="mb-3 inline-flex size-10 items-center justify-center rounded-xl bg-surface text-brand-green-ink">
          <KeyRound className="size-5" />
        </div>
        <DialogTitle>{t('login.forgot_title', 'Reset your password')}</DialogTitle>
        <DialogDescription>
          {t('login.forgot_desc', 'Password resets are handled by your workspace administrator. Enter your account email and send them a request — they can set a new password for you from Settings → Users.')}
        </DialogDescription>

        <div className="mt-1 space-y-4">
          <Field label={t('login.email', 'Email')} htmlFor="forgot-email">
            <Input
              id="forgot-email" type="email" autoComplete="email"
              placeholder="nama@perusahaan.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('login.close', 'Close')}
            </Button>
            <Button asChild>
              <a href={mailto}>
                <Mail className="size-4" /> {t('login.forgot_email_admin', 'Email an administrator')}
              </a>
            </Button>
          </div>

          <p className="text-caption text-stone">
            {t('login.forgot_note', 'After they set a new password, sign in and change it from your account settings.')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
